import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Shield, Loader2, Save, User } from 'lucide-react';
import { 
  childPrivacyService, 
  ChildPrivacySettings as ChildPrivacySettingsType 
} from '@/services/childPrivacyService';

interface ChildPrivacySettingsProps {
  childId: string;
  childUsername: string;
  parentId: string;
  onSaved?: () => void;
}

const ChildPrivacySettings: React.FC<ChildPrivacySettingsProps> = ({
  childId,
  childUsername,
  parentId,
  onSaved,
}) => {
  const [settings, setSettings] = useState<ChildPrivacySettingsType>({
    profileVisibility: 'private',
    showContactPublic: false,
    showAge: false,
    allowMessages: false,
    allowTeamInvites: true,
    showOnlineStatus: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [childId, parentId]);

  const loadSettings = async () => {
    setLoading(true);
    const result = await childPrivacyService.getChildPrivacySettings(childId, parentId);
    if (result.success && result.data) {
      setSettings(result.data);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await childPrivacyService.updateChildPrivacySettings(childId, parentId, settings);
    
    if (result.success) {
      toast.success(`Privacy settings updated for ${childUsername}`);
      onSaved?.();
    } else {
      toast.error(result.error || 'Failed to update privacy settings');
    }
    setSaving(false);
  };

  const updateSetting = <K extends keyof ChildPrivacySettingsType>(
    key: K, 
    value: ChildPrivacySettingsType[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="p-4 bg-gray-700 rounded-lg border border-gray-600">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-sports-green" />
          <span className="text-gray-300 text-sm">Loading privacy settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-gray-600 rounded-lg overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 bg-gray-700 hover:bg-gray-600 transition-colors flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-sports-green/20 rounded-full flex items-center justify-center">
            <User className="h-4 w-4 text-sports-green" />
          </div>
          <div className="text-left">
            <h4 className="font-medium text-white">{childUsername}'s Privacy</h4>
            <p className="text-xs text-gray-400">
              {settings.profileVisibility === 'public' ? 'Public Profile' : 
               settings.profileVisibility === 'team-only' ? 'Team Only' : 'Private Profile'}
            </p>
          </div>
        </div>
        <Shield className={`h-5 w-5 transition-transform ${expanded ? 'rotate-180' : ''} text-sports-green`} />
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="p-4 bg-gray-800 space-y-4">
          {/* Profile Visibility */}
          <div className="space-y-2">
            <Label className="text-gray-300 text-sm">Profile Visibility</Label>
            <Select 
              value={settings.profileVisibility} 
              onValueChange={(value: 'public' | 'team-only' | 'private') => 
                updateSetting('profileVisibility', value)
              }
            >
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                <SelectItem value="private">Private - Only family can see</SelectItem>
                <SelectItem value="team-only">Team Only - Teammates & coaches</SelectItem>
                <SelectItem value="public">Public - Everyone can see</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Toggle Options */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white text-sm">Make Profile Public</Label>
                <p className="text-xs text-gray-400">Allow everyone to view profile</p>
              </div>
              <Switch
                checked={settings.profileVisibility === 'public'}
                onCheckedChange={(checked) => 
                  updateSetting('profileVisibility', checked ? 'public' : 'private')
                }
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white text-sm">Show Contact Information</Label>
                <p className="text-xs text-gray-400">Display email/phone publicly</p>
                <p className="text-xs text-red-400">⚠️ Not recommended for minors</p>
              </div>
              <Switch
                checked={settings.showContactPublic}
                onCheckedChange={(checked) => updateSetting('showContactPublic', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white text-sm">Show Age</Label>
                <p className="text-xs text-gray-400">Display age on profile</p>
              </div>
              <Switch
                checked={settings.showAge}
                onCheckedChange={(checked) => updateSetting('showAge', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white text-sm">Allow Messages</Label>
                <p className="text-xs text-gray-400">Let others send direct messages</p>
              </div>
              <Switch
                checked={settings.allowMessages}
                onCheckedChange={(checked) => updateSetting('allowMessages', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white text-sm">Allow Team Invites</Label>
                <p className="text-xs text-gray-400">Receive team invitations</p>
              </div>
              <Switch
                checked={settings.allowTeamInvites}
                onCheckedChange={(checked) => updateSetting('allowTeamInvites', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white text-sm">Show Online Status</Label>
                <p className="text-xs text-gray-400">Show when child is online</p>
              </div>
              <Switch
                checked={settings.showOnlineStatus}
                onCheckedChange={(checked) => updateSetting('showOnlineStatus', checked)}
              />
            </div>
          </div>

          {/* Save Button */}
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-sports-green hover:bg-sports-green/90 text-black"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Privacy Settings
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ChildPrivacySettings;
